<div>
  <!-- Main Loading State -->
  <tix-loading-state
    *ngIf="isLoading || loadingError"
    [isLoading]="isLoading && !loadingError"
    [hasError]="!!loadingError"
    [errorMessage]="loadingError || ''"
    type="fullscreen"
    (retryClicked)="retryLoading()"
  >
  </tix-loading-state>

  <ng-container *ngIf="!isLoading && !loadingError">
    <tix-continuous-release-checker
      [companyId]="companyId"
      (redirect)="checkReleaseAndRedirect()"
    >
    </tix-continuous-release-checker>

    <div class="flex h-screen overflow-hidden">
      <!-- Mobile Sidebar Overlay -->
      <div
        *ngIf="showMobileSidebar"
        class="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
        (click)="closeMobileSidebar()"
      ></div>

      <!-- Sidebar -->
      <div
        class="bg-white shadow-lg lg:relative lg:shadow-md"
        [class]="getSidebarClasses()"
        [@slideInOut]="expandSidebar ? 'open' : 'closed'"
      >
        <tix-company-sidebar
          [expandSidebar]="expandSidebar"
          (expandSidebarChanged)="toggleSidebar($event)"
          (closeMobileSidebar)="closeMobileSidebar()"
        ></tix-company-sidebar>
      </div>

      <div class="flex-1 flex flex-col bg-primary-50 h-full w-full">
        <tix-header (toggleSidebar)="handleHeaderToggle()"></tix-header>
        <div class="flex-1 overflow-auto p-4">
          <ng-container *ngIf="isCompanyReady">
            <router-outlet></router-outlet>
          </ng-container>
          <ng-container *ngIf="!isCompanyReady">
            <!-- Company Context Loading State -->
            <tix-loading-state
              [isLoading]="true"
              [hasError]="false"
              type="mini"
              loadingSubText="Setting up company context..."
            >
            </tix-loading-state>
          </ng-container>
        </div>
      </div>
    </div>

    <ng-container *ngIf="isSubmitFeedbackOpen">
      <button
        class="feedback-btn"
        tix-icon-button
        (click)="closeSubmitFeedbackPopover()"
      >
        <tix-icon svgIcon="hero-close-solid" class="text-white"></tix-icon>
      </button>

      <div class="feedback-container" *ngIf="isSubmitFeedbackOpen">
        <div class="h-full w-full relative">
          <button class="close-btn" (click)="togglePopup()">
            <tix-icon
              [svgIcon]="'hero-close-solid'"
              class="text-[#1982B1]"
            ></tix-icon>
          </button>
          <form
            class="flex flex-col gap-2"
            *ngIf="!feedbackFormSubmited; else submited"
            [formGroup]="feedbackForm"
            #fileForm="ngForm"
            (ngSubmit)="submitFeedback()"
          >
            <h2 class="font-semibold">Submit Feedback</h2>

            <mat-form-field class="field">
              <mat-label>Title</mat-label>

              <input
                matInput
                type="text"
                placeholder="Title"
                formControlName="title"
              />
              <mat-error
                *ngIf="
                  titleControl?.hasError('required') && categoryControl?.touched
                "
              >
                Title is required
              </mat-error>
            </mat-form-field>

            <div>
              <textarea
                rows="5"
                placeholder="Description"
                class="focus:border-blue-500 h-auto border-2 border-gray-300 rounded-md bg-white resize-none !outline-none p-1 !focus:outline-none w-full overflow-hidden"
                formControlName="description"
                [ngClass]="{
                  'border-[#ef4444]':
                    descriptionControl?.hasError('required') &&
                    descriptionControl?.touched
                }"
              ></textarea>

              <mat-error
                *ngIf="
                  descriptionControl?.hasError('required') &&
                  categoryControl?.touched
                "
                class="ml-2 text-xs font-medium text-[#ef4444]"
              >
                Description is required
              </mat-error>
            </div>

            <mat-form-field>
              <mat-label>Select a category</mat-label>
              <mat-select formControlName="category">
                <mat-option
                  *ngFor="let category of feedbackCategories"
                  [value]="category.value"
                >
                  {{ category.label }}
                </mat-option>
              </mat-select>
              <mat-error
                *ngIf="
                  categoryControl?.hasError('required') &&
                  categoryControl?.touched
                "
              >
                Category is required
              </mat-error>
            </mat-form-field>

            <div *ngIf="selectedFiles.length > 0" class="mt-4 space-y-3">
              <div
                *ngFor="let file of selectedFiles; let i = index"
                class="flex items-center space-x-3 p-2 border rounded-lg bg-[#F8F9FB]"
              >
                <div
                  class="w-14 h-14 flex-shrink-0 bg-gray-100 flex items-center justify-center rounded overflow-hidden"
                >
                  <ng-container *ngIf="isImage(file); else videoThumbnail">
                    <img
                      [src]="file.preview"
                      alt="{{ file.name }}"
                      class="object-cover w-full h-full"
                    />
                  </ng-container>
                  <ng-template #videoThumbnail>
                    <video
                      [src]="file.preview"
                      class="object-cover w-full h-full"
                      muted
                    ></video>
                  </ng-template>
                </div>
                <div class="flex-grow">
                  <p class="text-sm font-medium text-gray-800">
                    {{ file.name }}
                  </p>
                </div>
                <button (click)="removeFile(i)" class="rounded-full">
                  <tix-icon
                    svgIcon="hero-close-solid"
                    class="text-[#BA1A1A]"
                  ></tix-icon>
                </button>
              </div>
            </div>
            <div class="w-full z-10">
              <label
                for="fileInput"
                class="flex flex-col items-center justify-center w-full h-16 border-2 border-dashed border-[#1982B1] rounded-lg cursor-pointer bg-white hover:bg-blue-50 transition"
              >
                <div class="flex gap-2 items-center justify-center">
                  <tix-icon svgIcon="upload" class="text-[#1982B1]"></tix-icon>
                  <p id="filePlaceholder" class="text-gray-600 text-sm">
                    Choose Files
                  </p>
                </div>
              </label>
              <input
                type="file"
                id="fileInput"
                name="fileInput"
                accept="image/*,video/*"
                class="sr-only"
                (change)="onFileSelected($event)"
                multiple
              />
              <mat-error
                *ngIf="isMoreThanOneVideoSelected"
                class="ml-2 text-xs font-medium text-[#ef4444]"
              >
                Only one video allowed.
              </mat-error>
              <mat-error
                *ngIf="isVideoSizeExceeded"
                class="ml-2 text-xs font-medium text-[#ef4444]"
              >
                Video size shouldn't exceeds 10mb.
              </mat-error>
            </div>

            <button
              type="submit"
              tix-button
              class="submit-btn"
              [disabled]="
                feedbackForm.invalid ||
                isSubmitting ||
                isMoreThanOneVideoSelected ||
                isVideoSizeExceeded
              "
            >
              <div role="status" *ngIf="isSubmitting">
                <svg
                  aria-hidden="true"
                  class="inline w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-gray-600 dark:fill-gray-300"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
              <span *ngIf="!isSubmitting">Submit</span>
            </button>
            <mat-error
              *ngIf="submittingError"
              class="ml-2 text-xs font-medium text-[#ef4444]"
            >
              We couldn't send your feedback, try again!
            </mat-error>
          </form>

          <ng-template #submited>
            <div
              class="text-center h-full flex flex-col items-center justify-center"
            >
              <tix-icon
                svgIcon="check-circle-solid"
                class="text-[#1982B1]"
              ></tix-icon>
              <h1 class="mt-4 text-lg font-bold text-gray-800">
                Feedback Submitted
              </h1>

              <p class="mt-2 text-sm text-gray-500">
                Thank you for your feedback! Your input helps us improve. Ready
                to share more?
              </p>

              <!-- Button -->
              <button class="mt-6" tix-button (click)="newFeedback()">
                Submit New Feedback
              </button>
            </div>
          </ng-template>
        </div>
      </div>
    </ng-container>
  </ng-container>
</div>
